import type { ScrollMeasurement } from "../scrollLayout";

/** All values in scroll.offset units (0..1 of the page's max scroll) so the
 *  existing consumers keep comparing against scroll.offset directly. */
export type DeckWindows = {
  d1: DeckAnimWindow;
  d2: DeckAnimWindow;
  /** Bundle boxes — full enter + exit, mirrors D2 so the bundle panel/boxes
   *  enter and exit with the same feel as the deck sections. */
  d3: DeckAnimWindow;
  text: {
    d1In: number;
    d1InEnd: number;
    d1Out: number;
    d1OutEnd: number;
    d2In: number;
    d2InEnd: number;
    d2Out: number;
    d2OutEnd: number;
    d3In: number;
    d3InEnd: number;
    d3Out: number;
    d3OutEnd: number;
  };
};
export type DeckAnimWindow = {
  animStart: number;
  enter: number;
  enterEnd: number;
  exit: number;
  exitEnd: number;
  animEnd: number;
};

/** Choreography fractions of each deck's visibility segment
 *  [anchorTop − vh, nextAnchorTop] — i.e. from "section starts entering the
 *  viewport from the bottom" to "next section reaches the viewport top".
 *  Baked from the previously tuned offset literals at DECKS_SCROLL_PAGES = 5
 *  (see docs/superpowers/plans/2026-06-11-content-driven-scroll.md); tune
 *  feel here. */
const D1 = {
  animStart: 0.163,
  enter: 0.237,
  enterEnd: 0.4,
  exit: 0.563,
  exitEnd: 0.711,
  animEnd: 0.756,
  textIn: 0.267,
  textInEnd: 0.4,
  textOut: 0.548,
  textOutEnd: 0.667,
} as const;
const D2 = {
  animStart: 0.131,
  enter: 0.208,
  enterEnd: 0.377,
  exit: 0.546,
  exitEnd: 0.7,
  animEnd: 1.162,
  textIn: 0.131,
  textInEnd: 0.269,
  textOut: 0.5,
  textOutEnd: 0.623,
} as const;
/** Bundle segment fractions of [bundle − vh, end] — mirrors D2 so the bundle
 *  panel/boxes enter and exit with the same feel as the deck sections. */
const D3 = {
  animStart: 0.131,
  enter: 0.208,
  enterEnd: 0.377,
  exit: 0.546,
  exitEnd: 0.7,
  animEnd: 1.0,
  textIn: 0.131,
  textInEnd: 0.269,
  textOut: 0.5,
  textOutEnd: 0.623,
} as const;

/** Mobile choreography. The default fractions were tuned around the staggered
 *  fan-card flight; mobile shows no cards (box only), so they need their own
 *  timing. Per user feedback the panels change SEQUENTIALLY (no in-place
 *  crossfade — the overlapping double-text read badly) with a short breather
 *  (~0.15 screens) between them, and each text holds fully visible only
 *  ~0.35 screens (at the 110/100/130vh sections) so the scroll never feels
 *  parked on a panel. */
const D1_MOBILE = {
  animStart: 0.186,
  enter: 0.233,
  enterEnd: 0.352,
  exit: 0.529,
  exitEnd: 0.648,
  animEnd: 0.667,
  textIn: 0.257,
  textInEnd: 0.352,
  textOut: 0.519,
  textOutEnd: 0.638,
} as const;
const D2_MOBILE = {
  animStart: 0.145,
  enter: 0.185,
  enterEnd: 0.295,
  exit: 0.48,
  exitEnd: 0.605,
  animEnd: 0.63,
  textIn: 0.195,
  textInEnd: 0.295,
  textOut: 0.47,
  textOutEnd: 0.595,
} as const;
const D3_MOBILE = {
  animStart: 0.104,
  enter: 0.139,
  enterEnd: 0.235,
  exit: 0.417,
  exitEnd: 0.526,
  animEnd: 0.548,
  textIn: 0.148,
  textInEnd: 0.235,
  textOut: 0.409,
  textOutEnd: 0.517,
} as const;

export type DeckWindowsVariant = "default" | "mobile";

/** Mirrors today's /decks DOM (hero 100vh + 36vh gap + 170vh + 160vh +
 *  bundle 130vh + ~70vh footer) — pre-measurement fallback. Deck order is
 *  mediumship first (slot D1), guidance second (slot D2). */
export const DEFAULT_DECKS_MEASUREMENT: ScrollMeasurement = {
  vh: 1000,
  contentHeight: 6660,
  anchors: { mediumship: 1360, guidance: 3060, bundle: 4660, end: 5960 },
};

export function deriveDeckWindows(
  m: ScrollMeasurement,
  variant: DeckWindowsVariant = "default",
): DeckWindows | null {
  const { vh } = m;
  if (vh <= 0) return null;
  const [D1V, D2V, D3V] =
    variant === "mobile" ? [D1_MOBILE, D2_MOBILE, D3_MOBILE] : [D1, D2, D3];
  // D1 = first deck section (mediumship), D2 = second (guidance).
  const mediumship = m.anchors.mediumship / vh;
  const guidance = m.anchors.guidance / vh;
  const end = m.anchors.end / vh;
  const bundle = (m.anchors.bundle ?? m.anchors.end) / vh;
  if (![guidance, mediumship, bundle, end].every(Number.isFinite)) return null;

  const maxScroll = Math.max(0.001, m.contentHeight / vh - 1);
  const toOffset = (screens: number) => screens / maxScroll;
  const seg = (start: number, len: number, f: number) => toOffset(start + f * len);

  const d1Start = mediumship - 1;
  const d1Len = Math.max(0.001, guidance - d1Start);
  const d2Start = guidance - 1;
  const d2Len = Math.max(0.001, bundle - d2Start);
  const d3Start = bundle - 1;
  const d3Len = Math.max(0.001, end - d3Start);

  return {
    d1: {
      animStart: seg(d1Start, d1Len, D1V.animStart),
      enter: seg(d1Start, d1Len, D1V.enter),
      enterEnd: seg(d1Start, d1Len, D1V.enterEnd),
      exit: seg(d1Start, d1Len, D1V.exit),
      exitEnd: seg(d1Start, d1Len, D1V.exitEnd),
      animEnd: seg(d1Start, d1Len, D1V.animEnd),
    },
    d2: {
      animStart: seg(d2Start, d2Len, D2V.animStart),
      enter: seg(d2Start, d2Len, D2V.enter),
      enterEnd: seg(d2Start, d2Len, D2V.enterEnd),
      exit: seg(d2Start, d2Len, D2V.exit),
      exitEnd: seg(d2Start, d2Len, D2V.exitEnd),
      animEnd: seg(d2Start, d2Len, D2V.animEnd),
    },
    d3: {
      animStart: seg(d3Start, d3Len, D3V.animStart),
      enter: seg(d3Start, d3Len, D3V.enter),
      enterEnd: seg(d3Start, d3Len, D3V.enterEnd),
      exit: seg(d3Start, d3Len, D3V.exit),
      exitEnd: seg(d3Start, d3Len, D3V.exitEnd),
      animEnd: seg(d3Start, d3Len, D3V.animEnd),
    },
    text: {
      d1In: seg(d1Start, d1Len, D1V.textIn),
      d1InEnd: seg(d1Start, d1Len, D1V.textInEnd),
      d1Out: seg(d1Start, d1Len, D1V.textOut),
      d1OutEnd: seg(d1Start, d1Len, D1V.textOutEnd),
      d2In: seg(d2Start, d2Len, D2V.textIn),
      d2InEnd: seg(d2Start, d2Len, D2V.textInEnd),
      d2Out: seg(d2Start, d2Len, D2V.textOut),
      d2OutEnd: seg(d2Start, d2Len, D2V.textOutEnd),
      d3In: seg(d3Start, d3Len, D3V.textIn),
      d3InEnd: seg(d3Start, d3Len, D3V.textInEnd),
      d3Out: seg(d3Start, d3Len, D3V.textOut),
      d3OutEnd: seg(d3Start, d3Len, D3V.textOutEnd),
    },
  };
}
